import React, {
  createContext,
  FC,
  useState,
  ReactNode,
  useEffect,
  useMemo,
} from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { routes } from "@app/routing";

import { Item } from "../models";
import {
  InstallmentItem,
  PaymentInstallmentItem,
  PaymentPlan,
} from "../models/apiControllerModel";
import { getPostbillLocalStorage } from "@app/postBill/utils/localStorage";
import { LoginMethod } from "../models/postBillHeap";

export interface PostBillContextProps {
  patientUid: string | null;
  setPatientUid: (patientUid: string) => void;
  selectedBills: Item[];
  setSelectedBills: (selectedBills: Item[]) => void;
  paidDirectPayments: Set<string>;
  setPaidDirectPayments: (value: Set<string>) => void;
  paidPaymentPlans: Set<string>;
  setPaidPaymentPlans: (value: Set<string>) => void;
  totalAmount: number;
  setTotalAmount: (totalAmount: number) => void;
  selectedPaymentPlan: PaymentPlan;
  setSelectedPaymentPlan: (paymentPlan: PaymentPlan) => void;
  installmentItem: InstallmentItem | null;
  setInstallmentItem: (installmentItem: InstallmentItem) => void;
  selectedPaymentPlanItems: PaymentInstallmentItem[] | [];
  setSelectedPaymentPlanItems: (
    paymentPlanItems: PaymentInstallmentItem[]
  ) => void;
  pageFrom: string | null;
  setPageFrom: (pageFrom: string) => void;
  loginMethod: LoginMethod;
  setLoginMethod: (loginMethod: LoginMethod) => void;
  paymentCompleted: boolean;
  setPaymentCompleted: (PaymentCompleted: boolean) => void;
}

export const PostBillContext = createContext<PostBillContextProps>({
  patientUid: null,
  setPatientUid: () => undefined,
  selectedBills: [],
  setSelectedBills: () => undefined,
  paidDirectPayments: new Set<string>(),
  setPaidDirectPayments: () => {},
  paidPaymentPlans: new Set<string>(),
  setPaidPaymentPlans: () => {},
  totalAmount: 0,
  setTotalAmount: () => undefined,
  selectedPaymentPlan: PaymentPlan.None,
  setSelectedPaymentPlan: () => undefined,
  installmentItem: null,
  setInstallmentItem: () => undefined,
  selectedPaymentPlanItems: [],
  setSelectedPaymentPlanItems: () => undefined,
  pageFrom: null,
  setPageFrom: () => undefined,
  loginMethod: LoginMethod.TOKENIZED,
  setLoginMethod: () => undefined,
  paymentCompleted: false,
  setPaymentCompleted: () => undefined,
});

interface PostBillProviderProps {
  children: ReactNode;
}

const PostBillProvider: FC<PostBillProviderProps> = ({ children }) => {
  const [patientUid, setPatientUid] = useState<string | null>(null);
  const [selectedBills, setSelectedBills] = useState<Item[]>([]);
  const [paidDirectPayments, setPaidDirectPayments] = useState<Set<string>>(
    new Set()
  );
  const [paidPaymentPlans, setPaidPaymentPlans] = useState<Set<string>>(
    new Set()
  );
  const [totalAmount, setTotalAmount] = useState<number>(0);
  const [selectedPaymentPlan, setSelectedPaymentPlan] = useState<PaymentPlan>(
    PaymentPlan.None
  );
  const [installmentItem, setInstallmentItem] =
    useState<InstallmentItem | null>(null);
  const [selectedPaymentPlanItems, setSelectedPaymentPlanItems] = useState<
    PaymentInstallmentItem[]
  >([]);
  const [paymentCompleted, setPaymentCompleted] = useState<boolean>(false);
  const [pageFrom, setPageFrom] = useState<string | null>(null);

  const navigate = useNavigate();
  const { pathname } = useLocation();
  const localAuthType = getPostbillLocalStorage("authType");
  const token = getPostbillLocalStorage("guestToken");

  const allowedGuestLoginRoutes = useMemo(
    () => [
      // history and help pages are not supported by guest and public login
      routes.billingCenter,
      routes.reviewBills,
      routes.billingPayment,
      routes.billingFailure,
      routes.billingConfirmation,
      routes.billingPaymentPlanDetail,
      routes.billingUpdatePayment,
      routes.billingPaymentSuccess,
    ],
    []
  );

  useEffect(() => {
    const isComingFromAllowedRoute = allowedGuestLoginRoutes.includes(pathname);

    if (
      isComingFromAllowedRoute &&
      !patientUid &&
      localAuthType === "guest" &&
      token
    ) {
      navigate(`${routes.postbillGuestLogin}?token=${token}`);
    } else if (
      isComingFromAllowedRoute &&
      !patientUid &&
      localAuthType === "public"
    ) {
      navigate(routes.billingCenterGuestLogin);
    }
  }, [patientUid, token, allowedGuestLoginRoutes, pathname]);

  const [loginMethod, setLoginMethod] = useState<LoginMethod>(
    LoginMethod.TOKENIZED
  );

  return (
    <PostBillContext.Provider
      value={{
        patientUid,
        setPatientUid,
        selectedBills,
        setSelectedBills,
        paidDirectPayments,
        setPaidDirectPayments,
        paidPaymentPlans,
        setPaidPaymentPlans,
        totalAmount,
        setTotalAmount,
        selectedPaymentPlan,
        setSelectedPaymentPlan,
        installmentItem,
        setInstallmentItem,
        selectedPaymentPlanItems,
        setSelectedPaymentPlanItems,
        pageFrom,
        setPageFrom,
        loginMethod,
        setLoginMethod,
        paymentCompleted,
        setPaymentCompleted,
      }}
    >
      {children}
    </PostBillContext.Provider>
  );
};

export default PostBillProvider;
